import * as React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'src/registry/ui/Button';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { useGetFinancialDocumentPdf } from 'src/features/financialDocuments/api/useGetFinancialDocumentPdf';

type FinancialDocumentPdfButtonProps = {
    documentId: string;
};

/**
 * Opens the document's PDF in a new tab. `document_file` is a short-lived signed URL and is null until the PDF
 * has been generated, so the URL is always asked for on click - the endpoint generates the PDF first if needed.
 */
const FinancialDocumentPdfButton = ({ documentId }: FinancialDocumentPdfButtonProps) => {
    const { formatMessage } = useIntl();
    const onError = useOnError();
    const { mutate: getPdf, isPending } = useGetFinancialDocumentPdf();

    const onClick = React.useCallback(() => {
        // Opened synchronously inside the click so popup blockers allow it; pointed at the PDF once it is known.
        const pdfWindow = window.open('', '_blank');

        getPdf(
            { documentId },
            {
                onSuccess: ({ url }) => {
                    if (!pdfWindow) {
                        // The blank window was blocked after all - fall back to a plain open.
                        window.open(url, '_blank', 'noopener,noreferrer');
                        return;
                    }
                    pdfWindow.opener = null;
                    pdfWindow.location.href = url;
                },
                onError: (error) => {
                    pdfWindow?.close();
                    onError(error);
                },
            },
        );
    }, [documentId, getPdf, onError]);

    return (
        <Button className="cursor-pointer" disabled={isPending} onClick={onClick}>
            <FontAwesomeIcon
                icon={isPending ? ICON_MAP.SPINNER : ICON_MAP.VIEW}
                className={isPending ? 'animate-spin' : ''}
            />
            <span className="hidden md:block">{formatMessage(financialDocumentsMessages.preview)}</span>
        </Button>
    );
};

export default FinancialDocumentPdfButton;
